import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  ITEM_UNIT_LABEL,
  ItemUnitSchema,
  type ItemAdvancedSettings,
  type ItemMargin,
  type MarginType,
  type SettingsLockedBy,
} from "@forgepoint/shared";
import { useItemDefaultValuesStore } from "@/store/itemDefaultValuesStore";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { AdvancedSettingsSection } from "./AdvancedSettingsSection";
import { AllowedOptionsSelect } from "./AllowedOptionsSelect";
import { SettingLimitInput } from "./SettingLimitInput";
import { ItemDefaultValuesSheet } from "./ItemDefaultValuesSheet";

/**
 * Formats a margin type enum value into a human-readable label.
 * @param marginType - The margin type to format
 * @returns Formatted display label
 */
function formatMarginType(marginType: MarginType): string {
  if (marginType === "outsideOperations") {
    return "Outside Ops";
  }
  if (marginType === "operations") {
    return "Inside Ops";
  }
  if (marginType === "manufactured") {
    return "Manufactured";
  }
  return marginType.charAt(0).toUpperCase() + marginType.slice(1);
}

/**
 * Formats a margin's amount for display as a percentage or dollar value.
 * @param margin - The item margin to format
 * @returns Formatted string like "10%" or "$5.00"
 */
function formatMarginValue(margin: ItemMargin): string {
  if (margin.marginAmount.type === "PERCENTAGE") {
    return `${margin.marginAmount.percentage}%`;
  }
  return `$${margin.marginAmount.amount}`;
}

interface MarginSummary {
  type: MarginType;
  label: string;
  value: string;
  overrideCount: number;
}

interface ItemDefaultValuesSettingsProps {
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
  /** Current (possibly unsaved) value of the BOM sequence numbers toggle */
  bomSequenceNumbersEnabled: boolean;
  /** Called when the BOM sequence numbers toggle changes */
  onBomSequenceNumbersChange: (enabled: boolean) => void;
  enableAllowance: boolean;
  onEnableAllowanceChange: (enabled: boolean) => void;
  advancedSettings: ItemAdvancedSettings;
  onAdvancedSettingsChange: (advancedSettings: ItemAdvancedSettings) => void;
  advancedSettingsLockedBy: SettingsLockedBy;
  isSaving: boolean;
}

/**
 * Settings panel for managing organization-level item default values (margins and yield),
 * BOM toggles and advanced item settings.
 * Uses the standard settings UX pattern with section labels, settings rows, and a sheet editor.
 * @param props - ItemDefaultValuesSettingsProps
 * @returns Item default values settings panel element
 */
export function ItemDefaultValuesSettings({
  isAdmin,
  bomSequenceNumbersEnabled,
  onBomSequenceNumbersChange,
  enableAllowance,
  onEnableAllowanceChange,
  advancedSettings,
  onAdvancedSettingsChange,
  advancedSettingsLockedBy: lockedBy,
  isSaving,
}: ItemDefaultValuesSettingsProps) {
  const defaults = useItemDefaultValuesStore((state) => state.defaults);
  const isLoading = useItemDefaultValuesStore((state) => state.isLoading);
  const isDeleting = useItemDefaultValuesStore((state) => state.isDeleting);
  const error = useItemDefaultValuesStore((state) => state.error);
  const fetchDefaults = useItemDefaultValuesStore(
    (state) => state.fetchDefaults,
  );
  const deleteDefaults = useItemDefaultValuesStore(
    (state) => state.deleteDefaults,
  );

  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    fetchDefaults();
  }, [fetchDefaults]);

  // ── Derived state ──
  const marginSummaries = useMemo((): MarginSummary[] => {
    if (!defaults?.margin || defaults.margin.length === 0) {
      return [];
    }

    const typeMap = new Map<
      MarginType,
      { defaultMargin?: ItemMargin; overrideCount: number }
    >();

    for (const m of defaults.margin) {
      const existing = typeMap.get(m.type);
      if (!existing) {
        typeMap.set(m.type, {
          overrideCount: 0,
        });
      }
      const entry = typeMap.get(m.type);
      if (!entry) {
        continue;
      }
      if (m.minQty === undefined) {
        entry.defaultMargin = m;
      } else {
        entry.overrideCount += 1;
      }
    }

    return Array.from(typeMap.entries())
      .filter(([, v]) => v.defaultMargin !== undefined)
      .map(([type, v]) => ({
        type,
        label: formatMarginType(type),
        value: v.defaultMargin ? formatMarginValue(v.defaultMargin) : "",
        overrideCount: v.overrideCount,
      }));
  }, [defaults]);

  const yieldSummary = useMemo(() => {
    if (!defaults?.yield || defaults.yield.length === 0) {
      return null;
    }
    const defaultYield = defaults.yield.find((y) => y.minQty === undefined);
    if (!defaultYield) {
      return null;
    }
    const overrideCount = defaults.yield.filter(
      (y) => y.minQty !== undefined,
    ).length;
    return {
      percentage: defaultYield.percentage,
      overrideCount,
    };
  }, [defaults]);

  const hasDefaults = marginSummaries.length > 0 || yieldSummary !== null;

  // ── Handlers ──

  /**
   * Updates a single advanced setting.
   * @param key - The setting to update
   * @param value - The setting's new value
   */
  const setAdvancedSetting = <K extends keyof ItemAdvancedSettings>(
    key: K,
    value: ItemAdvancedSettings[K],
  ) => {
    onAdvancedSettingsChange({ ...advancedSettings, [key]: value });
  };

  /**
   * Deletes all item default values after confirmation.
   */
  const handleDelete = async () => {
    const success = await deleteDefaults();
    if (success) {
      toast.success("Item defaults removed");
      return;
    }
    toast.error("Failed to remove item defaults");
  };

  // ── Early returns ──
  if (isLoading) {
    return <ItemDefaultsSkeleton />;
  }

  if (error) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* ── Default Margins ── */}
        <SectionLabel title="Default Margins" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="item-defaults-margin-configuration"
              label="Margin Configuration"
              description="Default margins applied when the agent creates items without explicit values."
            >
              <div className="flex items-center gap-2">
                <ConfiguredIndicator configured={marginSummaries.length > 0} />
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSheetOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                )}
              </div>
            </SettingsRow>

            {marginSummaries.length > 0 && (
              <div className="px-4 py-3.5">
                <div className="flex flex-wrap gap-2">
                  {marginSummaries.map((ms) => (
                    <Badge
                      key={ms.type}
                      variant="secondary"
                      className="text-xs font-normal transition-colors"
                    >
                      {ms.label}: {ms.value}
                      {ms.overrideCount > 0 && (
                        <span className="ml-1 text-muted-foreground">
                          (+{ms.overrideCount} qty)
                        </span>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* ── Default Yield ── */}
        <SectionLabel title="Default Yield" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="item-defaults-yield-rate"
              label="Yield Rate"
              description="Applied to new items when no yield rate is specified. 100% means no scrap."
            >
              <div className="flex items-center gap-2">
                <ConfiguredIndicator configured={yieldSummary !== null} />
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSheetOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                )}
              </div>
            </SettingsRow>

            {yieldSummary && (
              <div className="px-4 py-3.5">
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant="secondary"
                    className="text-xs font-normal transition-colors"
                  >
                    {yieldSummary.percentage}% yield rate
                    {yieldSummary.overrideCount > 0 && (
                      <span className="ml-1 text-muted-foreground">
                        (+{yieldSummary.overrideCount} qty)
                      </span>
                    )}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* ── Bill of Materials ── */}
        <SectionLabel title="Bill of Materials" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="item-defaults-bom-sequence-numbers"
              label="BOM Sequence Numbers"
              description="Show the optional sequence number field on BOM operations."
            >
              <Switch
                checked={bomSequenceNumbersEnabled}
                onCheckedChange={onBomSequenceNumbersChange}
                disabled={!isAdmin}
              />
            </SettingsRow>
          </div>
        </Card>

        {/* ── Dimensions ── */}
        <SectionLabel title="Dimensions" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="item-defaults-enable-allowance"
              label="Enable allowance dimension"
              description="Show the allowance field on items and quote lines. Unit conversions add the allowance to the item's length and width. Turning this off hides the field and keeps stored allowances in pricing."
            >
              <Switch
                checked={enableAllowance}
                onCheckedChange={onEnableAllowanceChange}
                disabled={!isAdmin}
              />
            </SettingsRow>
          </div>
        </Card>

        {/* ── Advanced Settings ── */}
        <AdvancedSettingsSection
          anchorIdPrefix="item-defaults-advanced"
          hasLockedSetting={Object.keys(lockedBy).length > 0}
        >
          <SettingsRow
            id="item-defaults-advanced-max-part-number-length"
            label="Maximum Part Number Length"
            description="Limits how many characters an item's part number can have. Leave empty for no limit."
            lockedBy={lockedBy.maxPartNumberLength}
          >
            <SettingLimitInput
              value={advancedSettings.maxPartNumberLength}
              onChange={(value) =>
                setAdvancedSetting("maxPartNumberLength", value)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.maxPartNumberLength}
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-max-purchase-cost-breaks"
            label="Maximum Purchase Cost Breaks"
            description="Limits how many purchase cost breaks an item can have. Leave empty for no limit."
            lockedBy={lockedBy.maxPurchaseCostBreaks}
          >
            <SettingLimitInput
              value={advancedSettings.maxPurchaseCostBreaks}
              onChange={(value) =>
                setAdvancedSetting("maxPurchaseCostBreaks", value)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.maxPurchaseCostBreaks
              }
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allow-operation-efficiency"
            label="Allow Efficiency Adjustments"
            description="Let BOM operations set an efficiency percentage that adjusts their run time."
            lockedBy={lockedBy.allowOperationEfficiency}
          >
            <Switch
              checked={advancedSettings.allowOperationEfficiency}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowOperationEfficiency", checked)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.allowOperationEfficiency
              }
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allow-operation-quantity-breaks"
            label="Allow Operation Quantity Breaks"
            description="Let BOM operations use different times or costs at different quantities."
            lockedBy={lockedBy.allowOperationQuantityBreaks}
          >
            <Switch
              checked={advancedSettings.allowOperationQuantityBreaks}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowOperationQuantityBreaks", checked)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.allowOperationQuantityBreaks
              }
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allow-operation-qty-per"
            label="Allow Operation Quantity Per"
            description="Let BOM operations run more or less than once per part."
            lockedBy={lockedBy.allowOperationQtyPer}
          >
            <Switch
              checked={advancedSettings.allowOperationQtyPer}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowOperationQtyPer", checked)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.allowOperationQtyPer}
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allow-component-fixed-quantity"
            label="Allow Fixed Component Quantities"
            description="Let BOM components use a fixed quantity that does not scale with the order quantity."
            lockedBy={lockedBy.allowComponentFixedQuantity}
          >
            <Switch
              checked={advancedSettings.allowComponentFixedQuantity}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowComponentFixedQuantity", checked)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.allowComponentFixedQuantity
              }
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allow-component-scrap"
            label="Allow Component Scrap"
            description="Let BOM components add a scrap percentage to their quantity."
            lockedBy={lockedBy.allowComponentScrap}
          >
            <Switch
              checked={advancedSettings.allowComponentScrap}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowComponentScrap", checked)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.allowComponentScrap}
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allow-component-cost-override"
            label="Allow Component Cost Overrides"
            description="Allow a BOM component to use a fixed cost instead of its item's cost. Meant for exceptional costing needs - update the underlying item's cost for routine changes. Turning this off keeps existing overrides."
            lockedBy={lockedBy.allowComponentCostOverride}
          >
            <Switch
              checked={advancedSettings.allowComponentCostOverride}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowComponentCostOverride", checked)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.allowComponentCostOverride
              }
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allow-component-costing-method-override"
            label="Allow Component Costing Method Overrides"
            description="Let BOM components use a costing method other than the component item's."
            lockedBy={lockedBy.allowComponentCostingMethodOverride}
          >
            <Switch
              checked={advancedSettings.allowComponentCostingMethodOverride}
              onCheckedChange={(checked) =>
                setAdvancedSetting(
                  "allowComponentCostingMethodOverride",
                  checked,
                )
              }
              disabled={
                !isAdmin ||
                isSaving ||
                !!lockedBy.allowComponentCostingMethodOverride
              }
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allow-component-operation-links"
            label="Allow Component-to-Operation Links"
            description="Let BOM components be linked to an operation instead of the start of the job."
            lockedBy={lockedBy.allowComponentOperationLinks}
          >
            <Switch
              checked={advancedSettings.allowComponentOperationLinks}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowComponentOperationLinks", checked)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.allowComponentOperationLinks
              }
            />
          </SettingsRow>
          <SettingsRow
            id="item-defaults-advanced-allowed-units"
            label="Allowed Units of Measure"
            description="Limits which units of measure an item can use."
            lockedBy={lockedBy.allowedItemUnits}
          >
            <AllowedOptionsSelect
              options={ItemUnitSchema.options.map((unit) => ({
                value: unit,
                label: ITEM_UNIT_LABEL[unit],
              }))}
              value={advancedSettings.allowedItemUnits}
              onChange={(value) =>
                setAdvancedSetting("allowedItemUnits", value)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.allowedItemUnits}
            />
          </SettingsRow>
        </AdvancedSettingsSection>

        {/* ── Actions ── */}
        {isAdmin && hasDefaults && (
          <div className="flex justify-end">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Remove All Defaults
              {isDeleting && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
            </Button>
          </div>
        )}

        {!hasDefaults && isAdmin && (
          <div className="flex justify-end">
            <Button onClick={() => setSheetOpen(true)}>
              Configure Defaults
            </Button>
          </div>
        )}
      </div>

      <ItemDefaultValuesSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
}

// ── Local Components ──

/**
 * Skeleton loading state that mirrors the layout of the item defaults settings panel.
 * Shows placeholder rows for the margins and yield sections.
 * @returns Skeleton layout element
 */
function ItemDefaultsSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-5 w-32" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SkeletonRow />
        </div>
      </Card>

      <Skeleton className="h-5 w-24" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SkeletonRow />
        </div>
      </Card>
    </div>
  );
}

/**
 * Single skeleton row matching the SettingsRow layout.
 * @returns Skeleton row element
 */
function SkeletonRow() {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-3 w-64" />
      </div>
      <Skeleton className="h-8 w-[140px] rounded-md" />
    </div>
  );
}

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}

/**
 * Small status indicator showing whether a setting has been configured.
 * Displays a green dot with "Configured" or muted "Not set" text.
 * @param props - Contains a configured boolean
 * @returns Status indicator element
 */
function ConfiguredIndicator({ configured }: { configured: boolean }) {
  if (configured) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Configured
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground/50">Not set</span>;
}
